import styled from '@emotion/styled'
import { FunctionComponent } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { Title_P1 } from '~/components/_styled/titles'
import { WithClassName } from '~/types/utils'
import { Input } from '../components/Input'
import { label, prefix } from '../components/layout/ProductTrackingIntegration'
import { ToggleInput } from '../components/Toggle'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  flex: 2 1 0%;
`

const ToggleLabel = styled.span`
  margin-left: 0.5rem;
`

const Preview = styled.div`
  ${Fonts.P2};
  color: ${Colors.Hurricane};
  font-style: italic;
`

const BidAdjustmentValue = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

type Props = WithClassName & {
  disabled?: boolean
  trackingLabel: `${prefix}-${label}`
}

const BaseBidAdjustment: FunctionComponent<Props> = ({ className, disabled, trackingLabel }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<{
    bidAdjustment: {
      enable: boolean
      adjustment?: number
    }
  }>()

  const { bidAdjustment } = useWatch({ control })
  const convertedBidAdjustment = ((bidAdjustment?.adjustment ?? 0) / 100).toLocaleString(undefined, {
    maximumFractionDigits: 4,
  })

  return (
    <Wrapper className={className}>
      <div>
        <Title_P1>Bid adjustment</Title_P1>
        <p>Adjust the bidder’s returned price when bidder returns gross CPMs.</p>
      </div>

      <div>
        <ToggleInput control={control} name="bidAdjustment.enable" disabled={disabled} trackingLabel={`${trackingLabel}-${bidAdjustment?.enable ? 'unset' : 'set'}`} />
        <ToggleLabel>Bid adjustment</ToggleLabel>
      </div>

      {bidAdjustment?.enable && (
        <BidAdjustmentValue>
          <Input
            label="% of adjustment"
            control={control}
            name="bidAdjustment.adjustment"
            disabled={disabled}
            error={errors.bidAdjustment?.adjustment?.message}
            type="number"
            min={1}
            step={0.01}
            rules={{
              validate: {
                min: (value) => !bidAdjustment?.enable || (Number(value) > 0 ? true : 'Should be greater than 0'),
              },
              required: bidAdjustment?.enable,
            }}
            iconRight={'percent'}
          />
          <Preview>Preview: a 1€ bid will turn into {convertedBidAdjustment}€</Preview>
        </BidAdjustmentValue>
      )}
    </Wrapper>
  )
}

export const BidAdjustment = styled(BaseBidAdjustment)``
